import { useEffect, useMemo, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
// material
import {
  Stack,
  TextField,
  FormControl,
  Card,
  Autocomplete,
  Box,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { LoadingButton, TabContext, TabPanel } from "@mui/lab";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useMatch,
} from "react-router-dom";
import { customerValidationSchema } from "../../util/validationSchemas";
import { Icon } from "@iconify/react";
import "../../styles/charger.css";
import { useAccess } from "src/hooks/useAccess.hook";
import { useSelectedCompany } from "src/hooks/useSelectedTeam";
import {
  customerFormTabs,
  hideForTabs,
  getCustomerDefaultValues,
} from "./customer.constant";
import { useMutate, useGetDetail, useAvailableVehicles } from "src/hooks/useFetch.hook";
import {
  customerTagsById,
  customerUrl,
  getCustomerById,
  assignVehicleToCustomer,
  unassignVehicleToCustomer,
} from "src/react-query/endPoints";
import { Loader } from "../Loader";
import { is } from "src/util/is";
import Address from "src/common/components/Address";
import { FEATURE } from "src/util/enums";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch, useSelector } from "react-redux";
import { RootTabItemStyles, RootTabStyle } from "src/theme/overrides/Tabs";
import CustomerTagsForms from "./CustomerTagsForms";
import { formDataSlice } from "src/store/formData/formatData.slice";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useDomainConfig } from "src/hooks/useDomainConfig";

export default function CustomerForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const newCustomerMatch = useMatch("/customer/new");
  const hasUpdateAccess = useAccess(FEATURE.CUSTOMERS, "U");
  // Sub-feature permissions for tags and vehicles
  const hasTagViewAccess = useAccess(FEATURE.TAG, "V");
  const hasVehicleViewAccess = useAccess(FEATURE.VEHICLE, "V");
  // Form is read-only when viewing an existing record without update access
  const isReadOnly = !newCustomerMatch && !!id && !hasUpdateAccess;
  const company = useSelectedCompany();
  const {
    data: customerTags,
    isFetching: isFetchingCustomerTags,
    refetch: refetchCustomerTags,
  } = useGetDetail("customerTags", { id });

  const {
    data: availableTagsList,
    isFetching: isFetchingAvailableTagsList,
    refetch: refetchAvailableTagsList,
  } = useGetDetail("availableTags", { id, disabled: isReadOnly || !hasTagViewAccess });
  const {
    data: availableVehicles,
    isFetching: isFetchingAvailableVehicles,
    refetch: refetchAvailableVehicles,
  } = useAvailableVehicles({ id, disabled: isReadOnly || !hasVehicleViewAccess });
  const {
    data: assignedVehicle,
    isFetching: isFetchingAssignedVehicle,
    refetch: refetchAssignedVehicle,
  } = useGetDetail("customerVehicle", { id });

  const { mutate, isLoading } = useMutate("customer");
  const { mutate: mutateCustomerTags, isLoading: isMutatingCustomerTags } =
    useMutate("customerTags");
  const { mutate: mutateVehicleAssignment, isLoading: isAssigningVehicle } =
    useMutate("vehicleAssignment");
  const { data: customerData, isFetching } = useGetDetail("customer", { id });
  const domainConfig = useDomainConfig();
  const onSuccess = () => {
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "",
        tabOpened: "",
      })
    );
    navigate("/customer");
  };

  const tabsToShow = useMemo(() => customerFormTabs, []);
  const hideSaveFor = useMemo(() => {
    let hideSaveFor = [...hideForTabs];
    if (newCustomerMatch && !id) {
      hideSaveFor = hideForTabs.filter((tabName) => tabName === "tabs");
    }
    return hideSaveFor;
  }, [id, newCustomerMatch]);

  const { open, data } = useSelector(({ dialog }) => dialog);
  const { tabOpened, tabOpenedFor } = useSelector(
    ({ tabsCurrentlyOpenedFor }) => tabsCurrentlyOpenedFor
  );
  const { formData, formDataOf } = useSelector(({ formData }) => formData);
  const [selectedTab, setSelectedTab] = useState(
    tabOpenedFor === "customer" && tabOpened ? tabOpened : "general"
  );
  const [selectedTagsToAttach, setSelectedTagsToAttach] = useState([]); //USED ONLY DURING CREATING A NEW CUSTOMER
  const [AreTagsGettingAttach, setAreTagsGettingAttached] = useState(false); //USED ONLY DURING CREATING A NEW CUSTOMER
  const [selectedVehicle, setSelectedVehicle] = useState(null); // For new vehicle assignment
  const [isAssigning, setIsAssigning] = useState(false);
  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };
  const refetchAllTheLists = () => {
    refetchCustomerTags();
    refetchAvailableTagsList();
  };

  // Handle vehicle assignment
  const handleVehicleAssignment = async () => {
    if (!selectedVehicle) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: "Please select a vehicle to assign",
          severity: "error",
        })
      );
      return;
    }

    setIsAssigning(true);

    await mutateVehicleAssignment(
      {
        url: assignVehicleToCustomer(id),
        method: "POST",
        body: { vehicleId: selectedVehicle.vehicle_id },
      },
      {
        onSuccess: () => {
          dispatch(
            alertSlice.actions.setAlert({
              open: true,
              message: "Vehicle assigned successfully",
              severity: "success",
            })
          );
          refetchAssignedVehicle();
          refetchAvailableVehicles();
          setSelectedVehicle(null);
        },
        onError: (error) => {
          dispatch(
            alertSlice.actions.setAlert({
              open: true,
              message: error.message || "Failed to assign vehicle",
              severity: "error",
            })
          );
        },
        onSettled: () => {
          setIsAssigning(false);
        },
      }
    );
  };

  // Handle vehicle unassignment
  const handleVehicleUnassignment = async () => {
    if (!assignedVehicle) return;

    setIsAssigning(true);

    await mutateVehicleAssignment(
      {
        url: unassignVehicleToCustomer(id), // Using your endpoint function
        method: "POST", // Matching your API specification
        body: { vehicleId: assignedVehicle.vehicle_id }, // Matching the request body in your cURL
      },
      {
        onSuccess: () => {
          dispatch(
            alertSlice.actions.setAlert({
              open: true,
              message: "Vehicle unassigned successfully",
              severity: "success",
            })
          );
          refetchAssignedVehicle();
          refetchAvailableVehicles();
        },
        onError: (error) => {
          dispatch(
            alertSlice.actions.setAlert({
              open: true,
              message: error.message || "Failed to unassign vehicle",
              severity: "error",
            })
          );
        },
        onSettled: () => {
          setIsAssigning(false);
        },
      }
    );
  };

  const handleTagSubmit = async (selectedTags, clearSelected) => {
    const ids = selectedTags.map((t) => t.tag);
    if (is.empty(ids)) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: "Please select tags to be added",
          severity: "error",
        })
      );
      return;
    }
    await mutateCustomerTags(
      {
        url: customerTagsById(id),
        method: "POST",
        body: { tagIds: ids },
      },
      {
        onSuccess: () => {
          dispatch(
            alertSlice.actions.setAlert({
              open: true,
              message: "Successfully associated user with selected tags",
              severity: "success",
            })
          );
          clearSelected();
          refetchAllTheLists();
        },
      }
    );
  };
  const handleTagAdding = async (selectedTags, clearSelected) => {
    setAreTagsGettingAttached(true);
    const allSelectedTags = [...selectedTags];
    setSelectedTagsToAttach([...selectedTagsToAttach, ...allSelectedTags]);
    clearSelected();
    setAreTagsGettingAttached(false);
  };

  const handleTagRemove = (tagId) => {
    const updatedTagsToAttach = selectedTagsToAttach.filter(
      ({ id }) => id !== tagId
    );
    setSelectedTagsToAttach(updatedTagsToAttach);
  };

  const onSubmit = async (customerData, { setSubmitting }) => {
    if (!customerData.email && !customerData.phone) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            "Please specify either email or phone number. Both can't be left blank.",
          severity: "error",
        })
      );
      setSubmitting(false);
      return;
    }
    if (!customerData.locationLatitude && !customerData.locationLongitude) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: "Please specify address as well",
          severity: "error",
        })
      );
      setSubmitting(false);
      return;
    }
    const mutateObject = {
      url: customerUrl,
      method: "POST",
    };

    const {
      locationLatitude: lat,
      locationLongitude: lng,
      city,
      state,
      street,
      zipCode: zip_code,
      country,
      companyId,
      firstName,
      lastName,
      email,
      phone,
    } = customerData;

    const customerReqBody = {
      firstName,
      lastName,
      email,
      phone,
      companyId,
      address: {
        lat,
        lng,
        city,
        state,
        street,
        zip_code,
        country,
      },
      domain: domainConfig.domain,
    };

    if (newCustomerMatch && is.nullOrUndefined(id)) {
      customerReqBody.ocppTagIds = selectedTagsToAttach.map((t) => t.tag);
    }
    if (!is.nullOrUndefined(id) && !newCustomerMatch) {
      mutateObject.method = "PUT";
      mutateObject.url = getCustomerById(id);
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: customerReqBody,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: getCustomerDefaultValues(company.id),
    validationSchema: customerValidationSchema,
    onSubmit: onSubmit,
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    resetForm,
    values,
    setErrors,
  } = formik;

  const handleCreatingNewTag = () => {
    dispatch(
      formDataSlice.actions.setFormData({
        formData: {
          ...values, //TEMPORARILY STORE FORM DATA IN REDUX
        },
        formDataOf: "customer",
      })
    );
  };

  useEffect(() => {
    if (!isFetching) {
      if (
        tabOpenedFor === "customer" &&
        !is.empty(formData) &&
        formDataOf === "customer"
      ) {
        resetForm({ values: { ...formData } });
        dispatch(
          formDataSlice.actions.setFormData({
            formData: {},
            formDataOf: "",
          })
        );
      }
    }
  }, [formData, resetForm, tabOpenedFor, dispatch, formDataOf, isFetching]);

  useEffect(() => {
    if (customerData) {
      const { lat, lng, ...c } = customerData;
      resetForm({
        values: { ...c, locationLatitude: lat, locationLongitude: lng },
      });
    }
  }, [customerData]);

  if (isFetching) {
    return <Loader />;
  }

  // if (!newCustomerMatch && is.empty(data)) {
  //   navigate("/dashboard");
  //   return null;
  // }

  return (
    <>
      <TabContext value={selectedTab}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={tabsToShow.length}
            onChange={handleTabChange}
          >
            {tabsToShow.map((tab) => (
              <RootTabItemStyles key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </RootTabStyle>
        </Stack>
        <Card sx={{ p: 2 }}>
          <FormikProvider value={formik}>
            <Form
              autoComplete="off"
              id="customer"
              noValidate
              onSubmit={handleSubmit}
            >
              <TabPanel value="general">
                <Grid container spacing={3} className="mb-5">
                  <Grid item xs={12} lg={12}>
                    <Typography
                      variant="subtitle1"
                      className="mb-2 text-uppercase"
                    >
                      User Info
                    </Typography>
                    <Divider className="text-muted" />
                  </Grid>
                  <Grid item xs={6} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        label="First Name"
                        size="small"
                        {...getFieldProps("firstName")}
                        disabled={isReadOnly}
                        required
                        error={Boolean(touched.firstName && errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        label="Last Name"
                        size="small"
                        {...getFieldProps("lastName")}
                        disabled={isReadOnly}
                        required
                        error={Boolean(touched.lastName && errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        label="Email"
                        size="small"
                        // required
                        {...getFieldProps("email")}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                        disabled={isReadOnly}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} lg={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        label="Phone"
                        size="small"
                        {...getFieldProps("phone")}
                        disabled={isReadOnly}
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </TabPanel>
              <TabPanel value="address">
                <Address
                  getFieldProps={getFieldProps}
                  setFieldValue={setFieldValue}
                  formValues={values}
                  displayedFor="customer"
                  disabled={isReadOnly}
                />
              </TabPanel>
              <TabPanel value="tags">
                <CustomerTagsForms
                  availableTagsList={availableTagsList}
                  customerTags={id ? customerTags : selectedTagsToAttach}
                  isMutatingCustomerTags={isMutatingCustomerTags}
                  refetchAllTheLists={refetchAllTheLists}
                  isFetchingCustomerTags={
                    id ? isFetchingCustomerTags : AreTagsGettingAttach
                  }
                  handleTagSubmit={id ? handleTagSubmit : handleTagAdding}
                  isNewCustomer={newCustomerMatch}
                  saveFormData={handleCreatingNewTag}
                  removeFunction={handleTagRemove}
                />
              </TabPanel>
              <TabPanel value="vehicle">
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      className="mb-2 text-uppercase"
                    >
                      Vehicle Assignment
                    </Typography>
                    <Divider className="text-muted" />
                  </Grid>

                  {assignedVehicle ? (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Currently Assigned Vehicle
                        </Typography>
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid #eee",
                            borderRadius: 1,
                          }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography>
                                <strong>Registration:</strong>{" "}
                                {assignedVehicle.registration_number}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography>
                                <strong>Make:</strong> {assignedVehicle.make}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography>
                                <strong>Model:</strong> {assignedVehicle.model}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography>
                                <strong>Type:</strong>{" "}
                                {assignedVehicle.vehicle_type}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <LoadingButton
                          variant="contained"
                          color="error"
                          onClick={handleVehicleUnassignment}
                          loading={isAssigning}
                          disabled={isReadOnly}
                        >
                          Unassign Vehicle
                        </LoadingButton>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="body1" gutterBottom>
                          No vehicle currently assigned
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                          Assign a Vehicle
                        </Typography>
                        <Autocomplete
                          options={availableVehicles || []}
                          getOptionLabel={(option) =>
                            `${option.registration_number} - ${option.make} ${option.model}`
                          }
                          value={selectedVehicle}
                          onChange={(event, newValue) => {
                            setSelectedVehicle(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Vehicle"
                              variant="outlined"
                              fullWidth
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <div>
                                <strong>{option.registration_number}</strong>
                                <div>
                                  {option.make} {option.model} (
                                  {option.vehicle_type})
                                </div>
                              </div>
                            </li>
                          )}
                          disabled={isReadOnly || isFetchingAvailableVehicles}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <LoadingButton
                          variant="contained"
                          onClick={handleVehicleAssignment}
                          loading={isAssigning}
                          disabled={!selectedVehicle || isReadOnly}
                        >
                          Assign Vehicle
                        </LoadingButton>
                      </Grid>
                    </>
                  )}
                </Grid>
              </TabPanel>

              {!hideSaveFor.includes(selectedTab) && (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ width: "100%" }}
                >
                  {!isReadOnly && (
                    <>
                      <Button
                        size="medium"
                        type="button"
                        variant="outlined"
                        component={RouterLink}
                        to="/customer"
                        startIcon={<Icon icon="eva:close-outline" />}
                        sx={{
                          px: 3,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        size="medium"
                        type="submit"
                        variant="contained"
                        disabled={isReadOnly}
                        loading={isSubmitting || isLoading}
                        startIcon={<Icon icon="eva:save-outline" />}
                        sx={{
                          px: 3,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 500,
                        }}
                      >
                        {isSubmitting || isLoading ? 'Saving...' : 'Save Changes'}
                      </LoadingButton>
                    </>
                  )}
                </Stack>
              )}
            </Form>
          </FormikProvider>
        </Card>
      </TabContext>
    </>
  );
}
